import { useState } from 'react';
import { Box, Typography, Button, Stack, Chip, IconButton } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Plus, Download, Edit, Trash2, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '@/router/paths';
import { useTeachers } from '@/hooks/useTeachers';
import { PageHeader } from '@/components/navigation/PageHeader';
import { TeacherFilter } from './components/TeacherFilter';
import { StatusBadge } from '@/components/feedback/StatusBadge';
import { DepartmentBadge } from './components/DepartmentBadge';
import { ConfirmationDialog } from '@/components/dialogs/ConfirmationDialog';
import { AppAvatar } from '@/components/layout/AppAvatar';
import useTeacherStore from '@/store/teacherStore';
import { useSchool } from '@/hooks/useSchool';
import toast from 'react-hot-toast';
import { usePermission } from '@/hooks/usePermission';
import { PERMISSIONS } from '@/constants/permissions';

export function TeacherListPage() {
  const navigate = useNavigate();
  const { teachers, loading } = useTeachers();
  const { schoolId } = useSchool();
  const deleteTeacher = useTeacherStore(s => s.deleteTeacher);
  const { can } = usePermission();
  const canDelete = can(PERMISSIONS.TEACHER_DELETE);

  const [filter, setFilter] = useState({ search: '', department: '', designation: '', status: '' });
  const [deleteId, setDeleteId] = useState(null);

  const filteredTeachers = teachers.filter((t) => {
    const matchSearch = filter.search === '' ||
      `${t.firstName} ${t.lastName} ${t.employeeId}`.toLowerCase().includes(filter.search.toLowerCase());
    const matchDept = filter.department === '' || t.department === filter.department;
    const matchDesig = filter.designation === '' || t.designation === filter.designation;
    const matchStatus = filter.status === '' || t.status === filter.status;
    return matchSearch && matchDept && matchDesig && matchStatus;
  });

  const handleDelete = async () => {
    try {
      await deleteTeacher(schoolId, deleteId);
      toast.success('Teacher deactivated successfully');
      setDeleteId(null);
    } catch (err) {
      toast.error('Failed to deactivate teacher');
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Teacher',
      flex: 1.5,
      minWidth: 220,
      renderCell: (params) => (
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <AppAvatar name={`${params.row.firstName} ${params.row.lastName}`} src={params.row.photo} size={32} />
          <Typography variant="body2" fontWeight={500}>
            {params.row.firstName} {params.row.lastName}
          </Typography>
        </Stack>
      )
    },
    { field: 'employeeId', headerName: 'ID', flex: 0.8, minWidth: 120 },
    {
      field: 'department',
      headerName: 'Department',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => <DepartmentBadge department={params.value} />
    },
    { 
      field: 'designation', 
      headerName: 'Designation', 
      flex: 1,
      minWidth: 150 
    },
    { field: 'phone', headerName: 'Phone', flex: 1, minWidth: 150 },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => <StatusBadge status={params.value} />
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={() => navigate(`${PATHS.TEACHERS}/${params.row.id}`)} color="primary">
            <Eye size={18} />
          </IconButton>
          <IconButton size="small" onClick={() => navigate(`${PATHS.TEACHERS}/${params.row.id}/edit`)} color="info">
            <Edit size={18} />
          </IconButton>
          {canDelete && (
            <IconButton size="small" onClick={() => setDeleteId(params.row.id)} color="error">
              <Trash2 size={18} />
            </IconButton>
          )}
        </Stack>
      )
    }
  ];

  return (
    <Box>
      <PageHeader
        title="Teachers"
        subtitle="Manage your school's teaching staff"
        actions={
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<Download size={20} />} sx={{ bgcolor: 'white' }}>
              Export
            </Button>
            <Button
              variant="contained"
              startIcon={<Plus size={20} />}
              onClick={() => navigate(PATHS.TEACHERS_ADD)}
            >
              Add Teacher
            </Button>
          </Stack>
        }
      />

      <TeacherFilter
        filter={filter}
        onFilterChange={setFilter}
        onReset={() => setFilter({ search: '', department: '', designation: '', status: '' })}
      />

      <Box sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2, overflow: 'hidden' }}>
        <DataGrid
          autoHeight
          rows={filteredTeachers}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
          sx={{ border: 0 }}
        />
      </Box>

      <ConfirmationDialog
        open={Boolean(deleteId)}
        title="Deactivate Teacher"
        message="Are you sure you want to deactivate this teacher? They will no longer be able to access the system, but their historical data will be preserved."
        confirmText="Deactivate"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </Box>
  );
}
